/**
 * useEntityRecordStoreUrlSync
 *
 * Reads `:entityId` / `:recordId` from the route and acquires the matching
 * keyed entity-record store instance for the lifetime of the route. Installs
 * the `navigate` reference into `entityRecordActions` so imperative writers
 * can trigger URL changes.
 *
 * Mount once at the route root for the entity browsing page. Returns the IDs
 * (so the page can open its `EntityRecordScope` without a second `useParams`
 * call).
 */
import { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    acquireEntityRecordStore,
    releaseEntityRecordStore,
} from './entityRecordStore';
import { entityRecordActions } from './entityRecordActions';

type EntityRecordRouteParams = {
    entityId?: string;
    recordId?: string;
};

export const useEntityRecordStoreUrlSync = (): EntityRecordRouteParams => {
    const { entityId, recordId } = useParams<EntityRecordRouteParams>();
    const navigate = useNavigate();

    useEffect(() => {
        entityRecordActions.navigate.setNavigate(navigate);
        return () => {
            entityRecordActions.navigate.setNavigate(null);
        };
    }, [navigate]);

    useEffect(() => {
        if (!entityId) return;
        acquireEntityRecordStore(entityId, recordId);
        return () => {
            releaseEntityRecordStore(entityId, recordId);
        };
    }, [entityId, recordId]);

    return { entityId, recordId };
};
